import SEO from '@/components/SEO';
import { Button } from '@/components/ui/button';
import { motion } from 'framer-motion';
import { AlertCircle, ArrowLeft, Home } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';

export default function PageNotFound() {
  const location = useLocation();
  const navigate = useNavigate();
  const pageName = location.pathname.substring(1);

  return (
    <div className="min-h-screen bg-primary flex items-center justify-center p-6 relative overflow-hidden">
      <SEO title="404 - Page Not Found | PrimeLogic Tech Solutions" />

      {/* Background decorations matching the theme */}
      <div className="absolute inset-0 bg-gradient-to-br from-primary via-primary/95 to-primary" />
      <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-primary/10 rounded-full blur-3xl" />
      <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-secondary/10 rounded-full blur-3xl" />

      <div className="max-w-xl w-full relative z-10 text-center">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
          className="bg-primary-foreground/5 border border-primary-foreground/10 p-10 md:p-14 rounded-3xl backdrop-blur-sm shadow-2xl"
        >
          {/* 404 Header */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="flex justify-center mb-8"
          >
            <div className="flex items-center justify-center w-24 h-24 rounded-xl bg-secondary/10 border border-secondary/20">
              <AlertCircle className="w-12 h-12 text-secondary" />
            </div>
          </motion.div>

          <h1 className="text-8xl md:text-9xl font-bold text-white mb-4 tracking-tighter">404</h1>

          <h2 className="text-2xl md:text-3xl font-semibold text-white mb-6">Page Not Found</h2>

          <p className="text-primary-foreground/70 text-lg mb-8 leading-relaxed max-w-md mx-auto">
            Oops! The page <span className="text-secondary font-medium">"{pageName}"</span> you are
            looking for doesn't exist or has been moved.
          </p>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button
              onClick={() => navigate(-1)}
              variant="outline"
              className="w-full sm:w-auto px-8 py-6 rounded-full border-primary-foreground/20 text-primary-foreground bg-primary-foreground/10 hover:bg-transparent hover:text-white"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              Go Back
            </Button>

            <Button
              onClick={() => navigate('/')}
              className="w-full sm:w-auto px-8 py-6 rounded-full bg-secondary hover:bg-secondary/90 text-white font-semibold"
            >
              <Home className="w-5 h-5 mr-2" />
              Back to Home
            </Button>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
